import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Loader2, Eye, EyeOff, ArrowLeft, Mail } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { authService } from '@/lib/auth';
import * as Sentry from '@sentry/browser'

export default function LoginPage() {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');
	const [resetEmail, setResetEmail] = useState('');
	const [isResetLoading, setIsResetLoading] = useState(false);
	const [resetSuccess, setResetSuccess] = useState(false);
	const [isResetOpen, setIsResetOpen] = useState(false);

	const navigate = useNavigate();
	const location = useLocation();

	useEffect(() => {
		// Check if user is authenticated already
		const userRole = localStorage.getItem('userRole');
		const userFullName = localStorage.getItem('userFullName');
		const userEmail = localStorage.getItem('userEmail');

		// If authenticated, redirect based on role
		if (userRole && userFullName && userEmail) {
			if (userRole === 'admin') {
				navigate('/admin/dashboard');
			} else {
				navigate('/dashboard');
			}
		}
	}, [navigate]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsLoading(true);
		setError('');



		try {
			const {
				userRole,
				userId,
				userFullName,
				userEmail,
			} = await authService.signIn(email, password);

			// Store user info in localStorage
			localStorage.setItem('userRole', userRole);
			localStorage.setItem('userId', userId || '');
			localStorage.setItem('userFullName', userFullName || '');
			localStorage.setItem('userEmail', userEmail || '');

			// Parse the "r" param from URL
			const searchParams = new URLSearchParams(location.search);
			const returnPath = searchParams.get('r');

			// If there's a return path, navigate there
			if (returnPath && returnPath !== '/auth/login') {
				navigate(decodeURIComponent(returnPath));
				return;
			}

			// Navigate based on role
			if (userRole === 'admin') {
				navigate('/admin/dashboard');
			} else {
				navigate('/dashboard');
			}
		} catch (err: any) {
			console.error('Login error:', err);

			Sentry.withScope((scope) => {
				scope.setContext('Transactional email error', {
					email,
				});

				Sentry.captureException(err);
			});

			setError(err.message || 'Invalid email or password. Please try again.');
		} finally {
			setIsLoading(false);
		}
	};

	const handlePasswordReset = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsResetLoading(true);
		setError('');

		try {
			await authService.resetPassword(resetEmail);
			setResetSuccess(true);
		} catch (err: any) {
			console.error('Password reset error:', err);

			Sentry.withScope((scope) => {
				scope.setContext('Transactional email error', {
					resetEmail,
				});

				Sentry.captureException(err);
			});

			setError(err.message || 'Failed to send password reset email. Please try again.');
		} finally {
			setIsResetLoading(false);
		}
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-[#254164] to-[#1a2d47] flex items-center justify-center p-4">
			<div className="w-full max-w-md">
				<div className="text-center">
					<Link to="/" className="inline-flex items-center text-white hover:text-[#d3d9e0] transition-colors mb-6">
						<ArrowLeft className="h-4 w-4 mr-2" />
						Back to Home
					</Link>
					{/* <div className="flex items-center justify-center mb-4">
            <Link
              to="/"
            >
              <img
                src="/108shippingservices-logo-trans-rect.png"
                alt="108 Shipping & Services"
                className="w-40 h-20"
              />
            </Link>
          </div> */}
				</div>

				<Card className="border-0 shadow-2xl">
					<CardHeader className="space-y-1">
						<CardTitle className="text-2xl font-bold text-center text-[#254164]">Welcome Back</CardTitle>
						<CardDescription className="text-center">
							Sign in to your account to access your dashboard
						</CardDescription>
					</CardHeader>
					<CardContent>
						<form onSubmit={handleSubmit} className="space-y-4">
							<div className="space-y-2">
								<Label htmlFor="email">Email</Label>
								<Input
									id="email"
									type="email"
									placeholder="Enter your email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									required
									className="border-gray-300 focus:border-[#254164] focus:ring-[#254164]"
								/>
							</div>

							<div className="space-y-2">
								<Label htmlFor="password">Password</Label>
								<div className="relative">
									<Input
										id="password"
										type={showPassword ? 'text' : 'password'}
										placeholder="Enter your password"
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										required
										className="border-gray-300 focus:border-[#254164] focus:ring-[#254164] pr-10"
									/>
									<button
										type="button"
										onClick={() => setShowPassword(!showPassword)}
										className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
									>
										{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
									</button>
								</div>
							</div>

							{error && (
								<Alert className="border-red-200 bg-red-50">
									<AlertDescription className="text-red-700">
										{error}
									</AlertDescription>
								</Alert>
							)}

							<Button
								type="submit"
								className="w-full bg-[#254164] hover:bg-[#1a2d47] text-white"
								disabled={isLoading}
							>
								{isLoading ? (
									<>
										<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										Signing in...
									</>
								) : (
									'Sign In'
								)}
							</Button>
						</form>

						<div className="mt-4 text-center">
							<Dialog open={isResetOpen} onOpenChange={setIsResetOpen}>
								<DialogTrigger asChild>
									<Button variant="link" className="text-[#254164] hover:underline p-0">
										Forgot your password?
									</Button>
								</DialogTrigger>
								<DialogContent>
									<DialogHeader>
										<DialogTitle>Reset Password</DialogTitle>
										<DialogDescription>
											Enter your email address and we'll send you a link to reset your password.
										</DialogDescription>
									</DialogHeader>

									{resetSuccess ? (
										<div className="text-center py-4">
											<Mail className="h-12 w-12 text-green-500 mx-auto mb-4" />
											<h3 className="text-lg font-medium text-gray-900 mb-2">Check your email</h3>
											<p className="text-gray-600">
												We've sent a password reset link to {resetEmail}
											</p>
											<Button
												onClick={() => {
													setIsResetOpen(false);
													setResetSuccess(false);
													setResetEmail('');
												}}
												className="mt-4"
											>
												Close
											</Button>
										</div>
									) : (
										<form onSubmit={handlePasswordReset} className="space-y-4">
											<div className="space-y-2">
												<Label htmlFor="reset-email">Email</Label>
												<Input
													id="reset-email"
													type="email"
													placeholder="Enter your email"
													value={resetEmail}
													onChange={(e) => setResetEmail(e.target.value)}
													required
												/>
											</div>

											<Button
												type="submit"
												className="w-full bg-[#254164] hover:bg-[#1a2d47]"
												disabled={isResetLoading}
											>
												{isResetLoading ? (
													<>
														<Loader2 className="mr-2 h-4 w-4 animate-spin" />
														Sending...
													</>
												) : (
													'Send Reset Link'
												)}
											</Button>
										</form>
									)}
								</DialogContent>
							</Dialog>
						</div>

						<div className="mt-6 text-center">
							<p className="text-sm text-gray-600">
								Need help? Contact{' '}
								<a href="mailto:support@108shipping.com" className="text-[#254164] hover:underline">
									support@108shipping.com
								</a>
							</p>
						</div>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}